import {
  DndContext,
  closestCenter,
  MouseSensor,
  TouchSensor,
  useSensor,
  useSensors,
  DragOverlay,
  MeasuringStrategy,
} from '@dnd-kit/core'
import {
  SortableContext,
  verticalListSortingStrategy,
  arrayMove,
} from '@dnd-kit/sortable'
import { useFormikContext, getIn } from 'formik'
import { useState, useEffect, useMemo } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import { useDeleteChecklistMutation } from 'apiv2'
import { taskModalSelector, templatesModalSelector } from 'store/modals'
import { getTaskDetailAction } from 'store/tasks'
import Button from 'ui/components/buttons/Button/'
import ButtonMoreActions from 'ui/components/buttons/ButtonMoreActions'
import Input from 'ui/components/forms/Input/'
import Icon from 'ui/components/Icon'
import ConfirmModal from 'ui/components/modals/ConfirmModal'
import { notifySuccess, notifyError } from 'ui/components/Notify'
import {
  CHECKLIST_VARIANT,
  MAX_CHECKLIST_NAME_LENGTH,
  CHECKLIST_TRANSFER_TYPE,
} from '../../constants'
import {
  getChecklistProgress,
  deleteInArray,
  makePoint,
  getNextPositionId,
} from '../../helpers'
import ChecklistItem from '../ChecklistItem'
import ChecklistMenuActions from '../ChecklistMenuActions'
import ChecklistRowGhost from '../ChecklistRowGhost'
import ChecklistSortableRow from '../ChecklistSortableRow'
import ChecklistTransferModal from '../ChecklistTransferModal'
import {
  ChecklistWrapper,
  ChecklistContainer,
  ChecklistTitleWrap,
  ChecklistItems,
  ChecklistTitleContainer,
  TitleButtonWrap,
  ChecklistTitle,
  ChecklistProgress,
  ChecklistProgressBar,
  ProgressCounter,
  ChecklistButton,
} from './styled'

const { TASK, TEMPLATE } = CHECKLIST_VARIANT

const Checklist = ({ checklistIndex, variant }) => {
  const dispatch = useDispatch()
  const [isRenaming, setIsRenaming] = useState(false)
  const [prevName, setPrevName] = useState('')
  const [confirmOpened, setConfirmOpened] = useState(false)
  const [checklistTransferModal, setChecklistTransferModal] = useState(false)
  const [activeId, setActiveId] = useState(null)
  const [transferType, setTransferType] = useState(CHECKLIST_TRANSFER_TYPE.COPY)
  const [name, setName] = useState('')
  const [menuOpenId, setMenuOpenId] = useState(null)

  const isTaskVariant = variant === TASK
  const isTemplateVariant = variant === TEMPLATE

  const formik = useFormikContext()
  const { values, setFieldValue, setFieldError, setFieldTouched } = formik

  const base = `checklists.${checklistIndex}`
  const namePath = `${base}.name`
  const pointsPath = `${base}.points`
  const editingPath = `${base}.__editing`

  const checklist = getIn(values, base)
  const points = getIn(values, pointsPath) ?? []
  const items = useMemo(() => points.map((point) => point.positionId), [points])

  const { isDone, total, percent } = getChecklistProgress(points)

  const { defaultTaskDetails } = useSelector(taskModalSelector)
  const { templateId } = useSelector(templatesModalSelector)
  const [deleteChecklist] = useDeleteChecklistMutation()
  const isNotNewTask = isTaskVariant && !!defaultTaskDetails
  const isNotNewTemplate = isTemplateVariant && !!templateId
  const canTransfer = isNotNewTask || isNotNewTemplate
  const transferTargetLabel = isTemplateVariant
    ? 'другой шаблон'
    : 'другую задачу'
  const defaultTaskId = defaultTaskDetails?.id

  const sensors = useSensors(
    useSensor(MouseSensor, { activationConstraint: { distance: 4 } }),
    useSensor(TouchSensor, { pressDelay: 120, pressThreshold: 6 })
  )

  const deleteChecklistConfirmOpen = () => setConfirmOpened(true)

  const handlerDeleteChecklist = async (idx) => {
    const lists = getIn(values, 'checklists') ?? []
    const newLists = deleteInArray(lists, idx)
    if (isNotNewTask && Boolean(lists[idx]?.id)) {
      try {
        await deleteChecklist({
          itemId: lists[idx].id,
        }).unwrap()
        setFieldValue('checklists', newLists)
        notifySuccess('Чек-лист удален')
        dispatch(getTaskDetailAction(defaultTaskId))
      } catch (error) {
        notifyError('Ошибка удаления чек-листа')
      } finally {
        setConfirmOpened(false)
      }
    } else {
      setFieldValue('checklists', newLists)
      setConfirmOpened(false)
      if (isNotNewTask) {
        notifySuccess('Чек-лист удален')
      }
    }
  }

  const addPointHandler = () => {
    const nextPosId = getNextPositionId(points)
    setFieldValue(pointsPath, [...points, makePoint(nextPosId)])
  }

  const saveTitle = () => {
    const raw = getIn(values, namePath)
    const current = String(raw ?? '').trim()

    if (current.length === 0) {
      setFieldTouched(namePath, true, false)
      setFieldValue(editingPath, true, false)
      return
    }

    const cropped = current.slice(0, MAX_CHECKLIST_NAME_LENGTH)
    setFieldValue(namePath, cropped, false)
    setName(cropped)
    setFieldError(namePath, undefined)
    setIsRenaming(false)
    setFieldValue(editingPath, false, false)
  }

  const getEmptyPointIndexes = (points) =>
    points.reduce((acc, point, i) => {
      if (!point.text.trim()) acc.push(i)
      return acc
    }, [])

  const markEmptyPointsErrors = (listIdx, emptyIdxs) => {
    emptyIdxs.forEach((pointIdx) => {
      const textPath = `checklists.${listIdx}.points.${pointIdx}.text`
      setFieldTouched(textPath, true, false)
      setFieldError(textPath, 'Нужно заполнить')
    })
  }

  const tryOpenTransfer = (type) => {
    const emptyIdxs = getEmptyPointIndexes(points)

    if (emptyIdxs.length) {
      markEmptyPointsErrors(checklistIndex, emptyIdxs)
      notifyError('Заполните пустые пункты чек-листа')
      return
    } else {
      setTransferType(type)
      setChecklistTransferModal(true)
    }
  }

  const cancelRenaming = () => {
    setFieldValue(namePath, prevName, false)
    setFieldError(namePath, undefined)
    setIsRenaming(false)
    setFieldValue(editingPath, false, false)
  }

  useEffect(() => {
    const value = getIn(values, namePath)
    if (!isRenaming) {
      setName(value ?? '')
    }
  }, [namePath, values, isRenaming])

  const onDragStart = (e) => setActiveId(e.active.id)

  const onDragEnd = (event) => {
    setActiveId(null)
    const { active, over } = event
    if (!over || active.id === over.id) return

    const oldIndex = points.findIndex((point) => point.positionId === active.id)
    const newIndex = points.findIndex((point) => point.positionId === over.id)
    if (oldIndex === -1 || newIndex === -1) return

    const next = arrayMove(points, oldIndex, newIndex)
    setFieldValue(pointsPath, next)
  }

  const copyPointHandler = (text) => {
    const nextPosId = getNextPositionId(points)
    setFieldValue(pointsPath, [
      ...points,
      makePoint(nextPosId, text.includes('(копия)') ? text : `${text} (копия)`),
    ])
  }

  return (
    <>
      <ChecklistWrapper>
        <ChecklistContainer>
          <ChecklistTitleWrap>
            <ChecklistTitleContainer>
              {isRenaming ? (
                <>
                  <Input
                    id={`checklist-${checklistIndex}`}
                    placeholder="Название чек-листа"
                    name={namePath}
                    {...formik.getFieldProps(namePath)}
                    meta={formik.getFieldMeta(namePath)}
                    autoFocus
                  />
                  <TitleButtonWrap>
                    <Button
                      type="button"
                      theme="colorNoneTitle"
                      icon={<Icon name="checkbox" size={14} />}
                      onClick={saveTitle}
                    />
                    <Button
                      type="button"
                      theme="colorNoneTitle"
                      icon={<Icon name="close" size={10} />}
                      onClick={cancelRenaming}
                    />
                  </TitleButtonWrap>
                </>
              ) : (
                <>
                  <ChecklistTitle>{name}</ChecklistTitle>
                  <ButtonMoreActions menuPosition="left">
                    {({ close }) => (
                      <ChecklistMenuActions
                        canTransfer={canTransfer}
                        transferTargetLabel={transferTargetLabel}
                        onRename={() => {
                          const current = String(getIn(values, namePath) ?? '')
                          setPrevName(current)
                          setIsRenaming(true)
                          setFieldError(namePath, undefined)
                          setFieldValue(editingPath, true, false)
                          close()
                        }}
                        onCopy={() =>
                          tryOpenTransfer(CHECKLIST_TRANSFER_TYPE.COPY)
                        }
                        onMove={() =>
                          tryOpenTransfer(CHECKLIST_TRANSFER_TYPE.MOVE)
                        }
                        onDelete={() => {
                          deleteChecklistConfirmOpen(checklistIndex)
                          close()
                        }}
                        close={close}
                      />
                    )}
                  </ButtonMoreActions>
                </>
              )}
            </ChecklistTitleContainer>

            {points.length > 0 && (
              <ChecklistProgress>
                <ChecklistProgressBar percent={percent} />
                <ProgressCounter>
                  {isDone}/{total}
                </ProgressCounter>
              </ChecklistProgress>
            )}
          </ChecklistTitleWrap>

          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragStart={onDragStart}
            onDragEnd={onDragEnd}
            measuring={{
              droppable: { strategy: MeasuringStrategy.Always },
            }}
          >
            <SortableContext
              items={items}
              strategy={verticalListSortingStrategy}
            >
              <ChecklistItems data-dragging={activeId ? 'true' : 'false'}>
                {points.map((point, pointIndex) => (
                  <ChecklistSortableRow
                    key={point.id ?? `pos-${point.positionId}`}
                    id={point.positionId}
                    disabled={menuOpenId === point.id}
                    data-menu-open={menuOpenId === point.id ? 'true' : 'false'}
                  >
                    <ChecklistItem
                      checklistIndex={checklistIndex}
                      pointIndex={pointIndex}
                      copyPoint={copyPointHandler}
                      variant={variant}
                      onMenuOpenChange={(open) => {
                        setMenuOpenId(
                          open
                            ? point.id
                            : menuOpenId === point.id
                              ? null
                              : menuOpenId
                        )
                      }}
                    />
                  </ChecklistSortableRow>
                ))}
              </ChecklistItems>
            </SortableContext>
            <DragOverlay adjustScale={false} dropAnimation={null}>
              {activeId !== null
                ? (() => {
                    const idx = points.findIndex(
                      (point) => point.positionId === activeId
                    )
                    if (idx === -1) return null
                    return (
                      <ChecklistRowGhost>
                        <ChecklistItem
                          checklistIndex={checklistIndex}
                          pointIndex={idx}
                          variant={variant}
                        />
                      </ChecklistRowGhost>
                    )
                  })()
                : null}
            </DragOverlay>
          </DndContext>
        </ChecklistContainer>

        <ChecklistButton>
          <Button
            type="button"
            title="Добавить пункт"
            theme="borderNone"
            icon={<Icon name="plus-tag" size={16} />}
            onClick={addPointHandler}
          />
        </ChecklistButton>
      </ChecklistWrapper>

      <ConfirmModal
        title="Вы уверены, что хотите удалить чек-лист?"
        open={confirmOpened}
        confirmHandler={() => handlerDeleteChecklist(checklistIndex)}
        handlerClose={() => setConfirmOpened(false)}
      />

      {(isTaskVariant || isNotNewTemplate) && (
        <ChecklistTransferModal
          variant={variant}
          type={transferType}
          open={checklistTransferModal}
          checklist={checklist}
          defaultTaskId={defaultTaskId}
          defaultTemplateId={templateId}
          onMoveSuccess={() => {
            const lists = getIn(values, 'checklists') ?? []
            const next = deleteInArray(lists, checklistIndex)
            setFieldValue('checklists', next, false)
          }}
          handlerClose={() => setChecklistTransferModal(false)}
        />
      )}
    </>
  )
}

export default Checklist
